import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EMPTY, map, Observable, switchMap } from 'rxjs';
import { UserConfigsService } from '../../../../services/user-configs.service';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { OkCancelData } from '../../../dialogs/ok-cancel/ok-cancel-dialog.model';
import { AccountResource } from '../../../../models/resource/account.resource';
import { Role } from '../../../../models/role.model';
import { Account } from '../../../../models/ui/account.model';
import { User } from '../../../../models/ui/user.model';
import { AccountManager } from '../../../../managers/account.manager';
import { CaseAccessManager } from '../../../../managers/case-access.manager';
import { CaseManager } from '../../../../managers/case.manager';
import { NotificationManager } from '../../../../managers/notification.manager';
import { Guid } from '../../../../models/guid';
import { AccountConfigComponent } from '../account-config/account-config.component';
import { ExportAccountsDialogComponent } from '../export-accounts-dialog/export-accounts-dialog.component';
import { ManageUserAccessDialogComponent, ManageUserAccessDialogData } from '../manage-user-access-dialog/manage-user-access-dialog.component';
import { MoveCaseDialogComponent } from '../move-case-dialog/move-case-dialog.component';
import { PatchDeltaObjectUtility } from '../../../../utilities/patch-delta-object.utility';
import { BaseComponent } from '../../../base.component';
import { ManageOpenAIAccountSettingDialogComponent } from '../manage-open-ai-account-setting-dialog/manage-open-ai-account-setting-dialog.component';

@Component({
    selector: 'lex-account-edit',
    templateUrl: './account-edit.component.html',
    styleUrls: ['./account-edit.component.scss'],
    standalone: false
})
export class AccountEditComponent extends BaseComponent implements OnChanges {

	@Input() selectedAccountId: string = '';
	@Input() searchTerm: string = '';

	public selectedAccount: Account;
	public accountForm: UntypedFormGroup;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private accountManager: AccountManager,
		private caseManager: CaseManager,
		private caseAccessManager: CaseAccessManager,
		private notificationManager: NotificationManager,
		private userConfigsService: UserConfigsService,
		private dialog: MatDialog) {
		super();
		this.accountForm = this.formBuilder.group({
			accountName: ['', [Validators.required, Validators.maxLength(256)]],
			isHibernated: new UntypedFormControl(false),
			message: ['', Validators.maxLength(256)],
			crmUrl: ['', Validators.maxLength(256)],
			accountComments: ['', Validators.maxLength(1024)],
			type: ['', Validators.maxLength(256)]
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.selectedAccountId && this.selectedAccountId !== this.selectedAccount?.accountId) {
			this.accountManager.getAccountExpanded(this.selectedAccountId).subscribe(data => {
				this.selectedAccount = data;
				this.resetForm();
			})
		}
	}

	//#region Role check for Admin -> Accounts standard functionalities
	public canEditAccount(): boolean {
		return this.userConfigsService.inRoles(Role.GlobalAccountAdvancedFeatureSet, Role.Administrator, Role.ProfessionalServices);
	}
	//#endregion Role check for Admin -> Accounts standard functionalities

	//#region Role check for Admin -> Accounts advanced functionalities
	public canConfigAccount(): boolean {
		return this.userConfigsService.inRoles(Role.OperationsExpertFeatureSet, Role.Operations, Role.Developer);
	}

	public canManageUserAccess(): boolean {
		return this.userConfigsService.inRoles(Role.GlobalAccountAdvancedFeatureSet, Role.Administrator);
	}

	public canMoveCase(): boolean {
		return this.userConfigsService.inRoles(Role.OperationsAdvancedFeatureSet, Role.Operations, Role.Administrator);
	}
	//#endregion Role check for Admin -> Accounts advanced functionalities

	public nonEmployeeCount(users: User[]): number {
		let count = 0;
		users.forEach(user => {
			if (!(user.roles.find(role => role.name === Role.Employee)))
				count++;
		})
		return count;
	}

	public updateAccountName(isHibernated: boolean): void {
		const currentAccountName = this.accountForm.controls['accountName'].value as string;
		if (isHibernated && !currentAccountName.toLowerCase().includes('-hibernated')) {
			this.accountForm.controls['accountName'].setValue(`${currentAccountName}-Hibernated`);
			this.accountForm.controls['accountName'].markAsDirty();
		} else if (!isHibernated && currentAccountName.toLowerCase().includes('-hibernated')) {
			this.accountForm.controls['accountName'].setValue(currentAccountName.replace(/-hibernated/i, ''));
			this.accountForm.controls['accountName'].markAsDirty();
		}
	}

	public updateAccount(): void {
		if (!this.accountForm.valid)
			return;

		const jobPatch = PatchDeltaObjectUtility.getDeltaObject<AccountResource>(this.accountForm.controls);

		if (jobPatch === null)
			return;

		this.accountManager.updateAccount(this.selectedAccount.accountId, jobPatch).pipe(
			switchMap(() => {
				return this.accountManager.getAccountExpanded(this.selectedAccount.accountId)
			}),
			map((account) => {
				this.selectedAccount = account;
				this.resetForm();
				this.accountManager.refreshAdminAccounts(this.searchTerm);
			})).subscribe(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'Account updated successfully.'
				});
			}, err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to update account- ${err}`, type: 'error'
				});
				this.resetForm();
			});
	}

	public cancel(): void {
		this.resetForm();
	}

	//#region Open dialogs for Admin Accounts specific functionalities

	public delete(): void {
		this.deleteAccount(this.selectedAccount.accountId).subscribe(() => {
			this.accountManager.refreshAdminAccounts(this.searchTerm);
			this.notificationManager.displaySnackBarMessage({
				message: 'Account deleted successfully.'
			});
		}, err => {
			this.notificationManager.displaySnackBarMessage({
				message: `Failed to delete account- ${err}`,
				type: 'error'
			});
		});
	}

	public configAccount(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedAccount;
		this.dialog.open(AccountConfigComponent, dialogConfig);
	}

	public manageUserAccess(): void {
		this.manageUserAccessDialog(this.selectedAccount).pipe(
			switchMap((accountId) => {
				return this.accountManager.getAccountExpanded(accountId)
			})).subscribe((account) => {
				this.selectedAccount = account;
				this.notificationManager.displaySnackBarMessage({
					message: 'User access for account updated successfully.'
				});
			}, err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to update user access for account- ${err}`,
					type: 'error'
				});
			});
	}

	public manageOpenAI(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedAccountId;
		this.dialog.open(ManageOpenAIAccountSettingDialogComponent, dialogConfig)
			.afterClosed()
			.subscribe();
	}

	public moveCase(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedAccount;
		this.dialog.open(MoveCaseDialogComponent, dialogConfig).afterClosed()
			.pipe(switchMap((lexbeCase) => {
				if (lexbeCase)
					return this.caseManager.updateCaseAccount(lexbeCase);
				return EMPTY;
			}))
			.subscribe((lexbeCase) => {
				this.selectedAccount.cases.push(lexbeCase);
				this.notificationManager.displaySnackBarMessage({
					message: 'Case moved successfully.'
				});
			}, err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to move case- ${err}`,
					type: 'error'
				});
			});
	}

	public exportAccounts(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedAccount;
		this.dialog.open(ExportAccountsDialogComponent, dialogConfig).afterClosed()
			.pipe(switchMap((exportValue) => {
				if (exportValue == 'all')
					return this.accountManager.exportAccounts(Guid.Empty);
				else if (exportValue == 'one')
					return this.accountManager.exportAccounts(this.selectedAccount.accountId);
			}))
			.subscribe((url) => {
				if (url) {
					window.open(url, '_blank');
				}
			});
	}


	private deleteAccount(accoutdId: string): Observable<void> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';

		dialogConfig.data = {
			title: `Delete Account Confirmation`,
			message: `The user will no longer have access to this account.`,
			showAcknowledge: true
		} as OkCancelData;

		return this.dialog.open(OkCancelDialogComponent, dialogConfig)
			.afterClosed()
			.pipe(
				switchMap((confirm) => {
					if (confirm)
						return this.accountManager.deleteAccount(accoutdId);
					return EMPTY;
				})
			);
	}

	private manageUserAccessDialog(account: Account, isNewAccount?: boolean): Observable<string> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = { selectedAccount: account, isNewAccount: isNewAccount } as ManageUserAccessDialogData;
		return this.dialog.open(ManageUserAccessDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((userIds) => {
					if (userIds)
						return this.caseAccessManager.updateAccountUserAccess(account.accountId, userIds);
					return EMPTY;
				}));
	}

	//#endregion Open dialogs for Admin Accounts specific functionalities

	private resetForm(): void {
		if (this.selectedAccount)
			this.accountForm.reset({
				accountName: { value: this.selectedAccount.accountName, disabled: !this.canEditAccount() },
				isHibernated: { value: this.selectedAccount.isHibernated, disabled: !this.canEditAccount() },
				message: { value: this.selectedAccount.message, disabled: !this.canEditAccount() },
				crmUrl: { value: this.selectedAccount.crmUrl, disabled: !this.canEditAccount() },
				accountComments: { value: this.selectedAccount.accountComments, disabled: !this.canEditAccount() },
				type: { value: this.selectedAccount.type, disabled: !this.canEditAccount() }
			});
	}
}
